import moment from 'moment';
import { GroupedSessions, SessionResponseShort } from '../interfaces/sessions';

/**
 * Checks if a string is a UUID (v4 format)
 */
export const isUUID = (str: string): boolean => {
  const uuidRegex = /^[0-9A-F]{8}-[0-9A-F]{4}-4[0-9A-F]{3}-[89AB][0-9A-F]{3}-[0-9A-F]{12}$/i;
  return uuidRegex.test(str);
};

/**
 * Formats a session name. If the name is a UUID, returns "New Chat {dd/mm/yyyy at hh:mm}"
 * using the created_at timestamp with user's timezone.
 * Otherwise returns the original name.
 */
export const formatSessionName = (
  name: string,
  createdAt: string,
): string => {
  // Check if name is a UUID or the incomplete template
  if (isUUID(name) || name === 'New Chat (${})') {
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const date = new Date(createdAt);
    
    // Format: New Chat dd/mm/yyyy at hh:mm
    const formatter = new Intl.DateTimeFormat('en-GB', {
      timeZone: userTimezone,
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    
    const parts = formatter.formatToParts(date);
    const day = parts.find((p) => p.type === 'day')?.value || '';
    const month = parts.find((p) => p.type === 'month')?.value || '';
    const year = parts.find((p) => p.type === 'year')?.value || '';
    const hour = parts.find((p) => p.type === 'hour')?.value || '';
    const minute = parts.find((p) => p.type === 'minute')?.value || '';
    
    return `New Chat ${day}/${month}/${year} at ${hour}:${minute}`;
  }
  
  return name;
};

export const groupSessionsByDate = (
  sessions: SessionResponseShort[]
): GroupedSessions[] => {
  let sessionsMap: {
    [date: string]: { [sessionId: string]: SessionResponseShort | null };
  } = {};

  sessions.forEach((session: SessionResponseShort) => {
    const relativeDate = transformDateToString(session.created_at);
    if (!sessionsMap[relativeDate]) {
      sessionsMap[relativeDate] = {};
    }
    if (!sessionsMap[relativeDate][session.id]) {
      sessionsMap[relativeDate][session.id] = null;
    }
    sessionsMap[relativeDate][session.id] = session;
  });

  return Object.entries(sessionsMap).map(([relativeDate, sessions]) => ({
    relativeDate,
    sessions: Object.values(sessions),
  }));
};

export const transformDateToString = (value: any): string => {
  if (value) {
    const momentValue = moment(value, moment.ISO_8601);
    const now = moment();

    const hours = now.diff(momentValue, 'hours');
    if (hours < 24) {
      return 'Today';
    }
    const daysAgo = now.diff(momentValue, 'days');
    if (daysAgo === 1) {
      return 'Yesterday';
    }
    if (daysAgo >= 2 && daysAgo <= 6) {
      return `${daysAgo} day${daysAgo !== 1 ? 's' : ''} ago`;
    }
    if (now.diff(momentValue, 'months') < 1) {
      const weeksAgo = now.diff(momentValue, 'weeks');
      if (weeksAgo === 1) {
        return 'Last week';
      }
      return `${weeksAgo} week${weeksAgo !== 1 ? 's' : ''} ago`;
    }
    if (now.diff(momentValue, 'years') < 1) {
      const monthsAgo = now.diff(momentValue, 'months');
      if (monthsAgo === 1) {
        return 'Last month';
      }
      return `${monthsAgo} month${monthsAgo !== 1 ? 's' : ''} ago`;
    }
    const yearsAgo = now.diff(momentValue, 'years');
    if (yearsAgo === 1) {
      return 'Last year';
    }
    return `${yearsAgo} year${yearsAgo !== 1 ? 's' : ''} ago`;
  }
  return '';
};
